import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { cn } from '../lib/utils';

const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://ia.discador.net';

const getProxiedAudioUrl = (url, callId = null) => {
  if (!url) return url;
  if (url.includes('storage.vapi.ai')) {
    const encodedUrl = btoa(unescape(encodeURIComponent(url)));
    let proxyUrl = `${baseUrl}/api/audio/proxy?url=${encodedUrl}`;
    if (callId) proxyUrl += `&callId=${encodeURIComponent(callId)}`;
    return proxyUrl;
  }
  return url;
};

const traduzirEndedReason = (endedReason) => {
  if (!endedReason) return 'N/A';
  const texto = String(endedReason).toLowerCase().trim();
  if (texto === 'customer-ended-call' || texto === 'customer ended call') return 'Cliente encerrou a chamada';
  if (texto === 'assistant-ended-call' || texto === 'assistant ended call') return 'Assistente encerrou a chamada';
  if (texto === 'customer-did-not-answer' || texto === 'customer did not answer') return 'Cliente não atendeu';
  if (texto === 'customer-busy' || texto === 'customer busy') return 'Cliente ocupado';
  if (texto === 'silence-timed-out' || texto === 'silence timed out') return 'Silêncio prolongado (timeout)';
  return String(endedReason)
    .replace(/\bcustomer\b/gi, 'Cliente')
    .replace(/\bended\b/gi, 'encerrou')
    .replace(/\bcall\b/gi, 'chamada')
    .replace(/\bassistant\b/gi, 'Assistente')
    .replace(/\bdid\b/gi, '')
    .replace(/\bnot\b/gi, 'não')
    .replace(/\banswer\b/gi, 'atendeu')
    .replace(/\bbusy\b/gi, 'ocupado')
    .replace(/\bfailed\b/gi, 'falhou')
    .replace(/\berror\b/gi, 'erro')
    .replace(/\btimeout\b/gi, 'tempo esgotado')
    .replace(/\bconnection\b/gi, 'conexão')
    .replace(/\bunavailable\b/gi, 'indisponível')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
};

const getStatusBadgeClass = (endedReason) => {
  if (!endedReason) return 'bg-muted text-muted-foreground border-border';
  const reason = String(endedReason).toLowerCase().trim();
  if (reason === 'customer-ended-call' || reason === 'customer ended call' || reason.includes('customer-ended-call') || reason.includes('customer ended call') || reason.includes('cliente encerrou'))
    return 'bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-950 dark:text-teal-200 dark:border-teal-900';
  if (reason === 'assistant-ended-call' || reason === 'assistant ended call' || reason.includes('assistant-ended-call') || reason.includes('assistant ended call') || reason.includes('assistente encerrou'))
    return 'bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-950 dark:text-teal-200 dark:border-teal-900';
  if (reason === 'customer-did-not-answer' || reason === 'customer did not answer' || reason.includes('customer-did-not-answer') || reason.includes('customer did not answer') || reason.includes('cliente não atendeu') || reason.includes('did not answer'))
    return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-200 dark:border-amber-900';
  if (reason === 'silence-timed-out' || reason === 'silence timed out' || reason.includes('silence-timed-out') || reason.includes('silence timed out') || reason.includes('silêncio') || reason.includes('timeout'))
    return 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950 dark:text-orange-200 dark:border-orange-900';
  return 'bg-muted text-muted-foreground border-border';
};

export default function CallLogDetailsModal({ call, onClose }) {
  const [activeTab, setActiveTab] = useState('transcricao');

  useEffect(() => {
    if (!call) return;
    setActiveTab('transcricao');
  }, [call?.call?.id, call?._id]);

  if (!call) return null;

  const handleClose = () => onClose();

  const transcript = call.transcript || call.artifact?.transcript || '';
  const recordingUrl = call.recordingUrl || call.artifact?.recordingUrl || call.artifact?.recording?.url;
  const messages = call.artifact?.messages || call.messages || [];
  const structuredOutputs = call.structuredOutputs || call.artifact?.structuredOutputs || {};
  const callId = call.call?.id || call._id || '';

  const formatTranscript = (transcriptText, tools = []) => {
    if (!transcriptText) return null;
    if (typeof transcriptText === 'string') {
      const lines = transcriptText.split('\n').filter(line => line.trim());
      if (lines.length === 0) return null;
      const result = lines.map((line, index) => {
        const parts = line.split(':');
        const speaker = parts[0]?.trim() || 'Sistema';
        const message = parts.slice(1).join(':').trim();
        const isUser = speaker.toLowerCase().includes('user') || speaker.toLowerCase().includes('cliente') || speaker.toLowerCase().includes('customer');
        return (
          <div key={index} className={`mb-3 flex ${isUser ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-lg rounded-lg p-3 text-sm ${isUser ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'}`}>
              <p className="font-bold text-sm mb-1">{speaker}</p>
              <p>{message || '(sem mensagem)'}</p>
            </div>
          </div>
        );
      });
      if (tools && tools.length > 0) {
        tools.forEach((tool, idx) => {
          const formatTime = (timestamp) => {
            if (!timestamp) return '';
            const date = new Date(timestamp);
            return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
          };
          const formatTimestamp = (timestamp, secondsFromStart) => {
            if (!timestamp) return '';
            const date = new Date(timestamp);
            const hours12 = date.getHours() % 12 || 12;
            const minutes = date.getMinutes().toString().padStart(2, '0');
            const seconds = date.getSeconds().toString().padStart(2, '0');
            const ampm = date.getHours() >= 12 ? 'PM' : 'AM';
            let durationStr = '';
            if (secondsFromStart !== undefined) {
              const mins = Math.floor(secondsFromStart / 60);
              const secs = (secondsFromStart % 60).toFixed(2);
              durationStr = ` (+${mins}:${secs.toString().padStart(5, '0')})`;
            }
            return `${hours12}:${minutes}:${seconds} ${ampm}${durationStr}`;
          };
          result.push(
            <div key={`tool-${idx}`} className="mb-2 flex justify-center">
              <div className={`rounded-lg p-2.5 text-xs border ${tool.success ? 'bg-gray-900 dark:bg-gray-800 border-green-500' : 'bg-gray-900 dark:bg-gray-800 border-red-500'}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div className="flex flex-col">
                      <div className="flex items-center gap-1.5">
                        <span className="text-white font-medium text-xs">{tool.name}</span>
                        <div className={`w-4 h-4 rounded-full flex items-center justify-center ${tool.success ? 'bg-green-500' : 'bg-red-500'}`}>
                          {tool.success ? <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg> : <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" /></svg>}
                        </div>
                      </div>
                      <span className={`text-xs mt-0.5 ${tool.success ? 'text-green-400' : 'text-red-400'}`}>{tool.success ? 'Concluído com sucesso' : 'Falhou'}</span>
                    </div>
                  </div>
                  {formatTime(tool.time) && <span className="text-gray-400 text-xs whitespace-nowrap">{formatTime(tool.time)}</span>}
                </div>
                {tool.secondsFromStart !== undefined && <div className="mt-1.5 text-gray-400 text-xs">{formatTimestamp(tool.time, tool.secondsFromStart)}</div>}
              </div>
            </div>
          );
        });
      }
      return result;
    }
    return null;
  };

  const extractTranscriptFromMessages = (messagesList, tools = []) => {
    if (!messagesList || !Array.isArray(messagesList)) return null;
    const toolsMap = new Map();
    tools.forEach(tool => toolsMap.set(tool.toolCallId, tool));
    const formatTime = (timestamp) => {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    };
    const formatTimestamp = (timestamp, secondsFromStart) => {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      const hours12 = date.getHours() % 12 || 12;
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const seconds = date.getSeconds().toString().padStart(2, '0');
      const ampm = date.getHours() >= 12 ? 'PM' : 'AM';
      let durationStr = '';
      if (secondsFromStart !== undefined) {
        const mins = Math.floor(secondsFromStart / 60);
        const secs = (secondsFromStart % 60).toFixed(2);
        durationStr = ` (+${mins}:${secs.toString().padStart(5, '0')})`;
      }
      return `${hours12}:${minutes}:${seconds} ${ampm}${durationStr}`;
    };
    const events = [];
    messagesList.forEach((msg, index) => {
      const role = msg.role?.toLowerCase();
      const timestamp = msg.time || msg.secondsFromStart || 0;
      const secondsFromStart = msg.secondsFromStart !== undefined ? msg.secondsFromStart : (timestamp / 1000);
      if ((role === 'user' || role === 'assistant' || role === 'bot') && msg.message) {
        events.push({ type: 'message', role, message: msg.message, timestamp, secondsFromStart, index });
      }
      if (role === 'tool_calls' && msg.toolCalls && Array.isArray(msg.toolCalls)) {
        msg.toolCalls.forEach(toolCall => {
          const tool = toolsMap.get(toolCall.id);
          events.push({
            type: 'tool_call',
            toolCallId: toolCall.id,
            toolName: toolCall.function?.name || 'unknown',
            tool,
            timestamp: msg.time || timestamp,
            secondsFromStart: msg.secondsFromStart !== undefined ? msg.secondsFromStart : secondsFromStart,
            index
          });
        });
      }
    });
    events.sort((a, b) => (a.secondsFromStart !== undefined ? a.secondsFromStart : a.timestamp / 1000) - (b.secondsFromStart !== undefined ? b.secondsFromStart : b.timestamp / 1000));
    const result = events.map((event, eventIndex) => {
      if (event.type === 'message') {
        const isUser = event.role === 'user';
        return (
          <div key={`msg-${event.index}-${eventIndex}`} className={`mb-3 flex ${isUser ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-lg rounded-lg p-3 text-sm ${isUser ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'}`}>
              <p className="font-bold text-sm mb-1">{isUser ? 'Cliente' : 'Assistente'}</p>
              <p>{event.message}</p>
            </div>
          </div>
        );
      }
      if (event.type === 'tool_call') {
        const tool = event.tool;
        const isSuccess = tool ? tool.success : true;
        const toolTime = tool?.time || event.timestamp || Date.now();
        const duration = tool?.secondsFromStart !== undefined ? tool.secondsFromStart : event.secondsFromStart;
        return (
          <div key={`tool-call-${event.toolCallId}-${eventIndex}`} className="mb-2 flex justify-center">
            <div className={`rounded-lg p-2.5 text-xs border ${isSuccess ? 'bg-gray-900 dark:bg-gray-800 border-green-500' : 'bg-gray-900 dark:bg-gray-800 border-red-500'}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div className="flex flex-col">
                    <div className="flex items-center gap-1.5">
                      <span className="text-white font-medium text-xs">{event.toolName}</span>
                      <div className={`w-4 h-4 rounded-full flex items-center justify-center ${isSuccess ? 'bg-green-500' : 'bg-red-500'}`}>
                        {isSuccess ? <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg> : <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" /></svg>}
                      </div>
                    </div>
                    <span className={`text-xs mt-0.5 ${isSuccess ? 'text-green-400' : 'text-red-400'}`}>{isSuccess ? 'Concluído com sucesso' : 'Falhou'}</span>
                  </div>
                </div>
                {formatTime(toolTime) && <span className="text-gray-400 text-xs whitespace-nowrap">{formatTime(toolTime)}</span>}
              </div>
              {duration !== undefined && <div className="mt-1.5 text-gray-400 text-xs">{formatTimestamp(toolTime, duration)}</div>}
            </div>
          </div>
        );
      }
      return null;
    });
    return result.length > 0 ? result : null;
  };

  const tools = call.tools || [];
  const transcriptFormatted = transcript ? formatTranscript(transcript, tools) : null;
  const messagesFormatted = messages.length > 0 ? extractTranscriptFromMessages(messages, tools) : null;
  const finalTranscript = transcriptFormatted || messagesFormatted;

  const tabTriggerClass = (id) =>
    cn(
      'inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      activeTab === id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
    );

  const overlay = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur"
      role="dialog"
      aria-modal="true"
      aria-labelledby="call-log-details-title"
    >
      <Card className="relative w-full max-w-2xl">
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle id="call-log-details-title">Detalhes da Chamada</CardTitle>
          <Button variant="ghost" size="icon" onClick={handleClose} type="button">
            <XMarkIcon className="h-5 w-5" />
          </Button>
        </CardHeader>
        <CardContent className="max-h-[75vh] overflow-y-auto pr-2 text-sm text-muted-foreground">
          <div className="space-y-2">
            <p>
              <strong className="text-foreground">ID da Chamada:</strong>{' '}
              <span className="font-mono text-xs">{call.call?.id || 'N/A'}</span>
            </p>
            <p><strong className="text-foreground">Cliente:</strong> {call.call?.customer?.number || call.call?.customer?.name || call.customer?.number || 'N/A'}</p>
            <p><strong className="text-foreground">Assistente:</strong> {call.assistantName || call.assistant?.name || call.assistant?.nome || call.call?.assistant?.name || 'N/A'}</p>
            <p>
              <strong className="text-foreground">Status Final:</strong>{' '}
              <Badge className={getStatusBadgeClass(call.endedReason)}>
                {traduzirEndedReason(call.endedReason)}
              </Badge>
            </p>
          </div>

          <div className="mt-4">
            <div className="grid h-10 w-full grid-cols-3 gap-1 rounded-md bg-muted p-1 text-muted-foreground" role="tablist">
              <button type="button" role="tab" aria-selected={activeTab === 'transcricao'} className={tabTriggerClass('transcricao')} onClick={() => setActiveTab('transcricao')}>
                Transcrição
              </button>
              <button type="button" role="tab" aria-selected={activeTab === 'audio'} className={tabTriggerClass('audio')} onClick={() => setActiveTab('audio')}>
                Áudio
              </button>
              <button type="button" role="tab" aria-selected={activeTab === 'estrutura-saida'} className={tabTriggerClass('estrutura-saida')} onClick={() => setActiveTab('estrutura-saida')}>
                Estrutura de Saída
              </button>
            </div>

            {activeTab === 'transcricao' && (
              <div className="mt-4 rounded-lg border border-border bg-muted/40 p-4 ring-offset-background focus-visible:outline-none" role="tabpanel">
                {finalTranscript ? finalTranscript : <p className="text-muted-foreground">Transcrição não disponível.</p>}
              </div>
            )}

            {activeTab === 'audio' && (
              <div className="mt-4 ring-offset-background focus-visible:outline-none" role="tabpanel">
                {recordingUrl ? (
                  <audio controls src={getProxiedAudioUrl(recordingUrl, callId)} className="w-full" />
                ) : (
                  <p className="text-muted-foreground">Áudio não disponível.</p>
                )}
              </div>
            )}

            {activeTab === 'estrutura-saida' && (
              <div className="mt-4 ring-offset-background focus-visible:outline-none" role="tabpanel">
                {Object.keys(structuredOutputs).length === 0 ? (
                  <p className="text-muted-foreground">Nenhuma estrutura de saída disponível para esta chamada.</p>
                ) : (
                  <div className="space-y-4">
                    {Object.entries(structuredOutputs).map(([outputId, output], index) => (
                      <div
                        key={outputId && String(outputId).trim() !== '' ? `so-${outputId}` : `so-idx-${index}`}
                        className="rounded-lg border border-border bg-muted/40 p-4"
                      >
                        <div className="flex items-start gap-3 mb-2">
                          <div className="flex-1">
                            <h4 className="font-semibold text-foreground mb-1">{output.name || 'Estrutura de Saída'}</h4>
                            {output.compliancePlan && <p className="text-xs text-muted-foreground mb-2">Plano de Compliance: {output.compliancePlan}</p>}
                          </div>
                        </div>
                        <div className="mt-3">
                          <p className="text-sm text-muted-foreground mb-1">Resultado:</p>
                          <div className="bg-background rounded-md p-3 border border-border">
                            {typeof output.result === 'object' ? (
                              <pre className="text-sm text-foreground whitespace-pre-wrap overflow-x-auto">{JSON.stringify(output.result, null, 2)}</pre>
                            ) : (
                              <p className="text-foreground whitespace-pre-wrap">{String(output.result || 'N/A')}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return createPortal(overlay, document.body);
}

export { traduzirEndedReason, getStatusBadgeClass };
